<template>
   <div class="contract-settings-page">
      <!-- ── Page Header ── -->
      <div class="ms-page-header">
         <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
            <div class="d-flex align-items-center gap-3">
               <div class="ms-page-header__icon">
                  <i class="fas fa-file-contract"></i>
               </div>
               <div>
                  <p class="ms-page-header__breadcrumb">Platform Settings <span class="ms-breadcrumb-sep">/</span> Contract Settings</p>
                  <h1 class="ms-page-header__title">Contract Settings</h1>
               </div>
            </div>
         </div>
      </div>

      <div class="card">
         <div class="card-body m-3">
            <div class="section-header mb-4">
               <h5 class="fw-bold" style="color: #435666;">Internal Signatory Configuration</h5>
               <p class="text-muted mb-0" style="font-size: 14px;">
                  Configure the signatory details that appear on the execution page of contracts.
               </p>
            </div>

            <hr class="mb-4" style="color: #dee2e6;">

            <div class="row">
               <!-- Form Section -->
               <div class="col-lg-6">
                  <div class="mb-4">
                     <label for="signatoryName" class="form-label fw-bold" style="color: #435666;">
                        Signatory Name <span class="text-danger">*</span>
                     </label>
                     <input type="text" class="form-control" id="signatoryName"
                        v-model="settings.signatory_name"
                        placeholder="e.g. Martin Boyd"
                        :disabled="isLoading || !$store.getters['auth/isGlobalSuperAdmin']">
                  </div>

                  <div class="mb-4">
                     <label for="signatoryTitle" class="form-label fw-bold" style="color: #435666;">
                        Signatory Title <span class="text-danger">*</span>
                     </label>
                     <input type="text" class="form-control" id="signatoryTitle"
                        v-model="settings.signatory_title"
                        placeholder="e.g. Director"
                        :disabled="isLoading || !$store.getters['auth/isGlobalSuperAdmin']">
                  </div>

                  <div class="mb-4">
                     <label for="notificationName" class="form-label fw-bold" style="color: #435666;">
                        Xodo Sign Requester Name
                     </label>
                     <input type="text" class="form-control" id="notificationName"
                        v-model="settings.notification_name"
                        placeholder="e.g. Vertex Cyber Security"
                        :disabled="isLoading || !$store.getters['auth/isGlobalSuperAdmin']">
                     <small class="text-muted" style="font-size: 12px;">
                        The sender name shown on Xodo Sign notification emails.
                     </small>
                  </div>

                  <div class="mb-4">
                     <label for="notificationEmail" class="form-label fw-bold" style="color: #435666;">
                        Xodo Sign Notification Email
                     </label>
                     <input type="email" class="form-control" id="notificationEmail"
                        v-model="settings.notification_email"
                        placeholder="e.g. contracts@vtxsecurity.com"
                        :disabled="isLoading || !$store.getters['auth/isGlobalSuperAdmin']">
                     <small class="text-muted" style="font-size: 12px;">
                        Xodo Sign notifications (document opened, signed, etc.) will be sent to this address. Use a mailing list to notify your team.
                     </small>
                  </div>

                  <div class="mb-4">
                     <label class="form-label fw-bold" style="color: #435666;">
                        Signature Image
                     </label>
                     <div class="signature-upload-area" :class="{ 'disabled-area': !$store.getters['auth/isGlobalSuperAdmin'] }" @click="$store.getters['auth/isGlobalSuperAdmin'] ? triggerFileInput() : null" @dragover.prevent @drop.prevent="$store.getters['auth/isGlobalSuperAdmin'] ? handleDrop($event) : null">
                        <input type="file" ref="fileInput" accept="image/png,image/jpeg,image/webp"
                           @change="handleFileSelect" style="display: none;">
                        <div v-if="!signaturePreview" class="upload-placeholder">
                           <i class="fa-solid fa-cloud-arrow-up fa-2x mb-2" style="color: #00a0c1;"></i>
                           <p class="mb-0 text-muted" style="font-size: 14px;">{{ $store.getters['auth/isGlobalSuperAdmin'] ? 'Click or drag to upload signature image' : 'No signature uploaded' }}</p>
                           <p v-if="$store.getters['auth/isGlobalSuperAdmin']" class="mb-0 text-muted" style="font-size: 12px;">PNG, JPG or WebP</p>
                        </div>
                        <div v-else class="signature-preview-wrapper">
                           <img :src="signaturePreviewSrc" alt="Signature Preview" class="signature-preview-img">
                           <button v-if="$store.getters['auth/isGlobalSuperAdmin']" type="button" class="btn-remove-signature" @click.stop="removeSignature" title="Remove signature">
                              <i class="fa-solid fa-xmark"></i>
                           </button>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- Preview Section -->
               <div class="col-lg-6">
                  <label class="form-label fw-bold" style="color: #435666;">Preview</label>
                  <div class="preview-card">
                     <div class="preview-header">
                        <p class="mb-1" style="font-size: 12px; font-weight: 600; color: #00a0c1;">
                           SIGNED for and on behalf of
                        </p>
                        <p style="font-size: 12px; font-weight: 700; color: #00a0c1;">
                           VERTEX CYBER SECURITY PTY LTD
                        </p>
                     </div>
                     <div class="preview-sig-area">
                        <img v-if="signaturePreview" :src="signaturePreviewSrc"
                           alt="Signature" class="preview-sig-img">
                        <div v-else class="preview-sig-placeholder">
                           <span class="text-muted" style="font-size: 12px; font-style: italic;">No signature uploaded</span>
                        </div>
                     </div>
                     <div class="preview-line"></div>
                     <p class="mb-0 fw-bold" style="font-size: 14px; color: #333;">
                        {{ settings.signatory_name || 'Signatory Name' }}
                     </p>
                     <p class="mb-0" style="font-size: 12px; color: #888;">
                        {{ settings.signatory_title || 'Title' }}
                     </p>
                     <p class="mb-0 mt-1" style="font-size: 12px; color: #888;">
                        Date: {{ currentDate }}
                     </p>
                  </div>
               </div>
            </div>

            <hr class="mt-2 mb-3" style="color: #dee2e6;">

            <div v-if="$store.getters['auth/isGlobalSuperAdmin']" class="d-flex justify-content-end">
               <button type="button" class="btn btn-info text-light fw-bold px-4"
                  @click="saveSettings" :disabled="isSaving || !isFormValid">
                  <i class="fa-solid fa-floppy-disk me-2"></i>
                  {{ isSaving ? 'Saving...' : 'Save Settings' }}
               </button>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
export default {
   data() {
      return {
         settings: {
            signatory_name: '',
            signatory_title: '',
            notification_name: '',
            notification_email: '',
            signature_image: '',
         },
         signaturePreview: null, // base64 string for preview
         isLoading: true,
         isSaving: false,
         currentDate: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      }
   },

   computed: {
      isFormValid() {
         return this.settings.signatory_name.trim() !== '' && this.settings.signatory_title.trim() !== '';
      },
      signaturePreviewSrc() {
         if (!this.signaturePreview) return '';
         // If it already starts with data:, use as-is
         if (this.signaturePreview.startsWith('data:')) return this.signaturePreview;
         // Otherwise wrap raw base64
         return 'data:image/png;base64,' + this.signaturePreview;
      }
   },

   created() {
      this.loadSettings();
   },

   methods: {
      async loadSettings() {
         this.isLoading = true;
         try {
            const response = await this.$axios.get('/contract-settings');
            if (response.data.success && response.data.settings) {
               const s = response.data.settings;
               this.settings.signatory_name = s.signatory_name || '';
               this.settings.signatory_title = s.signatory_title || '';
               this.settings.notification_name = s.notification_name || '';
               this.settings.notification_email = s.notification_email || '';
               if (s.signature_image) {
                  this.signaturePreview = s.signature_image;
               }
            }
         } catch (error) {
            console.error('Failed to load contract settings:', error);
            this.$toast.error('Failed to load contract settings');
         } finally {
            this.isLoading = false;
         }
      },

      async saveSettings() {
         if (!this.isFormValid) return;
         this.isSaving = true;

         const payload = {
            signatory_name: this.settings.signatory_name.trim(),
            signatory_title: this.settings.signatory_title.trim(),
            notification_name: this.settings.notification_name.trim(),
            notification_email: this.settings.notification_email.trim(),
         };

         // Only send signature_image if it was changed (has a new base64 value)
         if (this.signaturePreview) {
            // Strip data URI prefix if present — backend stores raw base64
            let raw = this.signaturePreview;
            if (raw.startsWith('data:')) {
               raw = raw.replace(/^data:image\/\w+;base64,/, '');
            }
            payload.signature_image = raw;
         }

         try {
            const response = await this.$axios.put('/contract-settings', payload);
            if (response.data.success) {
               this.$toast.success(response.data.message);
            } else {
               this.$toast.error(response.data.message || 'Failed to save');
            }
         } catch (error) {
            console.error('Failed to save contract settings:', error);
            this.$toast.error('Failed to save contract settings');
         } finally {
            this.isSaving = false;
         }
      },

      triggerFileInput() {
         this.$refs.fileInput.click();
      },

      handleFileSelect(event) {
         const file = event.target.files[0];
         if (file) this.processFile(file);
      },

      handleDrop(event) {
         const file = event.dataTransfer.files[0];
         if (file) this.processFile(file);
      },

      processFile(file) {
         if (!file.type.startsWith('image/')) {
            this.$toast.error('Please upload an image file (PNG, JPG, or WebP)');
            return;
         }
         if (file.size > 2 * 1024 * 1024) {
            this.$toast.error('Image must be under 2MB');
            return;
         }

         const reader = new FileReader();
         reader.onload = (e) => {
            this.signaturePreview = e.target.result; // data:image/...;base64,...
         };
         reader.readAsDataURL(file);
      },

      removeSignature() {
         this.signaturePreview = null;
         this.settings.signature_image = '';
         if (this.$refs.fileInput) {
            this.$refs.fileInput.value = '';
         }
      },
   },
}
</script>

<style scoped>
.contract-settings-page {
   max-width: 100%;
}

.section-header h5 {
   margin-bottom: 4px;
}

.signature-upload-area {
   border: 2px dashed #dee2e6;
   border-radius: 12px;
   padding: 24px;
   text-align: center;
   cursor: pointer;
   transition: all 0.2s ease;
   min-height: 120px;
   display: flex;
   align-items: center;
   justify-content: center;
   background: #fafbfc;
}

.signature-upload-area:hover {
   border-color: #00a0c1;
   background: #f0fafc;
}

.upload-placeholder {
   display: flex;
   flex-direction: column;
   align-items: center;
}

.signature-preview-wrapper {
   position: relative;
   display: inline-block;
}

.signature-preview-img {
   max-height: 80px;
   max-width: 100%;
   object-fit: contain;
}

.btn-remove-signature {
   position: absolute;
   top: -8px;
   right: -8px;
   width: 24px;
   height: 24px;
   border-radius: 50%;
   background: #dc3545;
   color: white;
   border: 2px solid white;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 12px;
   cursor: pointer;
   transition: transform 0.2s ease;
   box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

.btn-remove-signature:hover {
   transform: scale(1.1);
}

/* Preview Card */
.preview-card {
   background: #fff;
   border: 1px solid #e9ecef;
   border-radius: 12px;
   padding: 24px;
   box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.preview-line {
   border-bottom: 1px solid #333;
   margin-bottom: 8px;
   width: 250px;
}

.preview-sig-area {
   min-height: 65px;
   display: flex;
   align-items: flex-end;
}

.preview-sig-img {
   max-height: 60px;
   object-fit: contain;
}

.preview-sig-placeholder {
   height: 60px;
   display: flex;
   align-items: center;
}

/* Page Header Styles */
.ms-page-header {
   margin-bottom: 24px;
   padding: 20px 24px;
   background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
   border: 1px solid rgba(0, 130, 152, 0.15);
   border-left: 5px solid #008298;
   border-radius: 12px;
   box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
   display: flex;
   align-items: center;
   gap: 16px;
}

.ms-page-header__icon {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: rgba(0, 130, 152, 0.08);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   color: #008298;
}

.ms-page-header__title {
   font-size: 1.45rem;
   font-weight: 800;
   color: #00363f;
   margin: 0;
   letter-spacing: -0.3px;
}

.ms-page-header__breadcrumb {
   font-size: 0.76rem;
   color: #557d84;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.8px;
   margin-bottom: 4px;
}

.ms-breadcrumb-sep {
   margin: 0 6px;
   color: rgba(0, 130, 152, 0.3);
}
</style>
